import Button from '@betterfunnel/ui-core/Button';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { FunnelIcon } from '@heroicons/react/24/outline';
import {
    CheckIcon,
    FunnelIcon as FilledFunnelIcon,
} from '@heroicons/react/24/solid';
import React, { useCallback, useMemo } from 'react';
import { capitalize, classNames } from '../../utils';

interface Props {
  children:
    | React.ReactElement<FilterButtonOptionProps<any>>[]
    | React.ReactElement<FilterButtonOptionProps<any>>;
}

export const FilterButton = ({ children }: Props) => {
  React.Children.forEach(children, (child) => {
    if (
      child.type !== FilterButtonOption &&
      (child.type as any).name !== 'FilterButtonOption'
    ) {
      throw new Error('All children must be of type FilterButtonOption');
    }
  });

  const allFiltersCount = useMemo(() => {
    let count = 0;
    React.Children.forEach(children, (child) => {
      count += child.props.selected.length;
    });
    return count;
  }, [children]);

  const onResetButtonClick = useCallback(() => {
    React.Children.forEach(children, (child) => {
      child.props.onChange([]);
    });
  }, [children]);

  return (
    <Popover className="relative h-full">
      <PopoverButton
        as={Button}
        variant="secondary"
        color="white"
        size="normal"
        className="w-full"
        icon={
          allFiltersCount ? (
            <FilledFunnelIcon className="h-4 w-4" />
          ) : (
            <FunnelIcon className="h-4 w-4" />
          )
        }
      >
        Filter{allFiltersCount ? ` / ${allFiltersCount}` : ''}
      </PopoverButton>
      <PopoverPanel
        transition
        anchor="bottom end"
        modal
        className="z-10 transition duration-200 ease-in-out data-closed:translate-y-1 data-closed:opacity-0"
      >
        <div className="absolute right-0 z-20 ml-[1.2rem] mr-3.5 mt-0.5 h-4 w-4 rotate-45 border-l border-t border-gray-300 bg-white dark:border-slate-600 dark:bg-slate-800"></div>
        <div className="mt-2.5 rounded-lg border border-gray-300 bg-white p-3 shadow-xs">
          <div className="relative flex gap-4">
            <Button
              variant="ternary"
              color="primary"
              size="x-small"
              disabled={!allFiltersCount}
              className={classNames(
                allFiltersCount ? 'visible' : 'invisible',
                'absolute -right-1.5',
              )}
              onClick={onResetButtonClick}
            >
              Reset
            </Button>

            {children}
          </div>
        </div>
      </PopoverPanel>
    </Popover>
  );
};

export interface FilterButtonOptionProps<T> {
  title: string;
  selected: string[];
  options: T[];
  getOptionKey: (option: T) => string;
  getOptionLabel: (option: T) => string;
  onChange: (selected: string[]) => void;
  noOptionsMessage?: React.ReactNode;
}

export function FilterButtonOption<T>({
  title,
  selected,
  options,
  getOptionKey,
  getOptionLabel,
  onChange,
  noOptionsMessage,
}: FilterButtonOptionProps<T>) {
  return (
    <div className="flex min-w-28 flex-col gap-2">
      <h6>{title}</h6>

      <div className="flex max-h-44 flex-col gap-1 overflow-scroll">
        {options.map((option) => {
          const optionKey = getOptionKey(option);
          const isSelected = selected.some((t) => t === optionKey);
          return (
            <Button
              key={optionKey}
              variant={isSelected ? 'primary' : 'secondary'}
              size="small"
              onClick={() => {
                onChange(
                  isSelected
                    ? selected.filter((t) => t !== optionKey)
                    : [...(selected || []), optionKey],
                );
              }}
              className="relative justify-start pe-9 ps-4"
            >
              <CheckIcon
                className={classNames(
                  'absolute right-2 h-4 w-4',
                  isSelected ? 'visible' : 'invisible',
                )}
              />

              {capitalize(getOptionLabel(option))}
            </Button>
          );
        })}
        {options.length === 0 ? (
          <p className="text-sm text-gray-500">
            {noOptionsMessage ?? 'No Options'}
          </p>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
}
